import { useState } from "react";
import { Play, Quote } from "lucide-react";
import bullioFace from "@/assets/bullio-face.png";
import { useVideoThumbnail } from "@/hooks/useVideoThumbnail";
import type { PlayableTestimonial } from "@/hooks/useTestimonials";
import { cn } from "@/lib/utils";

type Props = {
  testimonial: PlayableTestimonial;
  /** The first card on screen: its poster is worth fetching eagerly. */
  priority?: boolean;
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

/** Autoplay only after the viewer has clicked, which is the gesture browsers require. */
const withAutoplay = (embedUrl: string): string => {
  try {
    const url = new URL(embedUrl);
    url.searchParams.set("autoplay", "1");
    return url.toString();
  } catch {
    return embedUrl;
  }
};

/**
 * One testimonial: a still image with a play button, swapped for the real
 * player on click.
 *
 * The iframe is deliberately not rendered up front. A page with six
 * testimonials would otherwise load six YouTube or Vimeo players (several
 * hundred KB of third-party script each) before the visitor has watched
 * anything, and would hand every one of them a request from the visitor's
 * browser on page load. Clicking is the point at which that becomes a fair
 * trade.
 */
const TestimonialVideoCard = ({ testimonial, priority = false }: Props) => {
  const { video, poster_url, student_name, student_title, quote } = testimonial;
  const [playing, setPlaying] = useState(false);

  // Poster candidates, best first: the admin's own image, then the provider's
  // high-resolution still, then the low-resolution one that always exists.
  // These can all be named without asking anyone. YouTube supplies two;
  // Vimeo supplies none.
  const knownPosters = [poster_url, video.posterUrl, video.posterFallbackUrl].filter(
    (src): src is string => !!src,
  );
  const [posterIndex, setPosterIndex] = useState(0);
  const [posterLoaded, setPosterLoaded] = useState(false);

  // Vimeo only tells you where its picture is if you ask, and asking is a
  // request from the visitor's browser to a third party. So the card asks only
  // when it has run out of images it already knew about: straight away for a
  // Vimeo video with no cover image, and not at all for one whose cover image
  // loads. The answer goes last, after the ones that cost nothing to try.
  const exhausted = posterIndex >= knownPosters.length;
  const lookedUp = useVideoThumbnail(exhausted ? video.thumbnailLookupUrl : null).data ?? null;
  const posters = lookedUp ? [...knownPosters, lookedUp] : knownPosters;
  const poster = posters[posterIndex] ?? null;

  const allow =
    video.provider === "youtube"
      ? "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      : "autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media";

  return (
    <figure className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-white shadow-sm transition-all duration-300 hover:border-secondary/30 hover:shadow-xl hover:shadow-primary/5">
      {/*
        The media absorbs the leftover height instead of the caption. Cards in
        a row are stretched to the tallest one, and a card whose admin wrote no
        pull quote has no caption to fill that space, so it used to end in a
        blank white strip that read as a failed render. The spacer below fixes
        16:9 as the natural size and `flex-1` lets the still image grow past it
        and crop, which is what the extra space should look like.
      */}
      <div className="relative w-full flex-1 overflow-hidden bg-primary/5">
        <div aria-hidden className="aspect-video w-full" />
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={withAutoplay(video.embedUrl)}
            title={`${student_name}: testimonial video`}
            allow={allow}
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${student_name}'s testimonial video`}
            className="absolute inset-0 flex h-full w-full items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
          >
            {/*
              The branded panel is always the bottom layer, not just the
              fallback. A card therefore never shows an empty box while its
              picture is on the way (the picture can be a lookup plus a
              download away), and it is already what the card should look like
              if the picture never arrives. Its marks go once the picture has
              loaded, so they are not left showing through the fade.
            */}
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-secondary"
            >
              {!posterLoaded && (
                <>
                  <img
                    src={bullioFace}
                    alt=""
                    className="absolute -bottom-8 -right-6 h-40 w-40 rounded-full object-cover opacity-10"
                    draggable={false}
                  />
                  <span className="absolute left-5 top-5 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-base font-bold text-white backdrop-blur-sm">
                    {initialsOf(student_name)}
                  </span>
                </>
              )}
            </div>

            {poster && (
              <img
                data-testid="testimonial-poster"
                src={poster}
                alt=""
                aria-hidden
                loading={priority ? "eager" : "lazy"}
                decoding="async"
                // React 18 does not map camelCase fetchPriority to the DOM
                // attribute, it warns and drops it. Spread the real attribute
                // name instead of shipping a prop the browser never sees.
                {...{ fetchpriority: priority ? "high" : "auto" }}
                // Fades in over the panel once it has actually arrived,
                // instead of painting in from the top line by line.
                onLoad={() => setPosterLoaded(true)}
                // maxresdefault is not generated for every YouTube video, and an
                // admin poster can 404 too. Each failure steps down the list
                // rather than leaving a broken image on the home page. When the
                // list runs out the panel underneath is what the visitor sees.
                onError={() => {
                  setPosterLoaded(false);
                  setPosterIndex((i) => i + 1);
                }}
                className={cn(
                  "absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-500 motion-reduce:transition-none group-hover:scale-105",
                  posterLoaded ? "opacity-100" : "opacity-0",
                )}
                draggable={false}
              />
            )}

            {/* Scrim keeps the play button and the name readable over any still. */}
            <span
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/10 to-transparent transition-opacity duration-300 group-hover:from-primary/90"
            />

            <span
              aria-hidden
              className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-2xl ring-1 ring-black/5 transition-all duration-300 group-hover:scale-110 group-hover:bg-white sm:h-[72px] sm:w-[72px]"
            >
              <Play size={26} className="ml-1 fill-secondary text-secondary" />
            </span>

            <span className="absolute inset-x-0 bottom-0 flex items-end p-4 text-left">
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-white drop-shadow-sm sm:text-base">
                  {student_name}
                </span>
                {student_title && (
                  <span className="block truncate text-xs text-white/80">{student_title}</span>
                )}
              </span>
            </span>
          </button>
        )}
      </div>

      {quote && (
        <figcaption className="p-5">
          <blockquote className="relative pl-6 text-sm leading-relaxed text-muted-foreground">
            <Quote
              size={16}
              aria-hidden
              className="absolute left-0 top-0.5 shrink-0 fill-secondary/15 text-secondary/40"
            />
            <span style={{ textWrap: "pretty" }}>{quote}</span>
          </blockquote>
        </figcaption>
      )}
    </figure>
  );
};

export default TestimonialVideoCard;
